/**
 * Bit-level plumbing for share codes (`codec.ts`): a bit writer and reader with Elias-gamma and
 * varint numbers and packed text, base64url, and a CRC-16. Pure.
 */

/** Reading past the end or a nonsense value throws this; `decodeCave` turns it into a reason. */
export class BadCode extends Error {}

/** Text characters that pack into 6 bits; code 63 escapes any other character (16 bits). */
const TEXT = ' abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const TEXT_CODE = new Map([...TEXT].map((c, i) => [c, i]));
const TEXT_ESCAPE = 63;

/** floor(log2(v)) for v >= 1, in integers (the pure code avoids Math.log2). */
function gammaLength(v: number): number {
  let len = 0;
  while (2 ** (len + 1) <= v) len++;
  return len;
}

/** Bits Elias gamma spends on n >= 1. */
export function gammaBits(n: number): number {
  return gammaLength(Math.max(1, n)) * 2 + 1;
}

export class BitWriter {
  private readonly out: number[] = [];
  private acc = 0;
  private n = 0;

  /** The low `count` bits of `v` (count <= 24), most significant first. */
  bits(v: number, count: number): void {
    for (let i = count - 1; i >= 0; i--) {
      this.acc = (this.acc << 1) | (Math.floor(v / 2 ** i) & 1);
      if (++this.n === 8) {
        this.out.push(this.acc);
        this.acc = 0;
        this.n = 0;
      }
    }
  }

  /** Elias gamma: n >= 1; 1 costs one bit, 2-3 three, 4-7 five. */
  gamma(n: number): void {
    const v = Math.max(1, Math.floor(n));
    const len = gammaLength(v);
    this.bits(0, len);
    for (let i = len; i >= 0; i--) this.bits(Math.floor(v / 2 ** i) & 1, 1);
  }

  /** Any integer: zigzag, then five bits a group with a continuation bit. Arithmetic, so 2^32 survives. */
  int(n: number): void {
    const i = Number.isFinite(n) ? Math.round(n) : 0;
    let v = i >= 0 ? i * 2 : -i * 2 - 1;
    for (;;) {
      const more = v >= 32;
      this.bits(more ? 1 : 0, 1);
      this.bits(v % 32, 5);
      if (!more) return;
      v = Math.floor(v / 32);
    }
  }

  text(s: string): void {
    this.int(s.length);
    for (let i = 0; i < s.length; i++) {
      const code = TEXT_CODE.get(s[i] ?? '');
      if (code !== undefined) this.bits(code, 6);
      else {
        this.bits(TEXT_ESCAPE, 6);
        this.bits(s.charCodeAt(i), 16);
      }
    }
  }

  /** The bytes so far, the last one padded with zero bits. */
  bytes(): number[] {
    return this.n === 0 ? [...this.out] : [...this.out, this.acc << (8 - this.n)];
  }
}

export class BitReader {
  private pos = 0;
  constructor(private readonly data: Uint8Array, private readonly end: number) {}

  bit(): number {
    if (this.pos >= this.end * 8) throw new BadCode('the code is cut short');
    const b = ((this.data[this.pos >> 3] ?? 0) >> (7 - (this.pos & 7))) & 1;
    this.pos++;
    return b;
  }

  bits(count: number): number {
    let v = 0;
    for (let i = 0; i < count; i++) v = v * 2 + this.bit();
    return v;
  }

  gamma(): number {
    let len = 0;
    while (this.bit() === 0) if (++len > 30) throw new BadCode('the code is damaged (a number is too long)');
    let v = 1;
    for (let i = 0; i < len; i++) v = v * 2 + this.bit();
    return v;
  }

  int(): number {
    let v = 0;
    let scale = 1;
    for (let group = 0; group < 11; group++) {
      const more = this.bit();
      v += this.bits(5) * scale;
      if (more === 0) return v % 2 === 0 ? v / 2 : -(v + 1) / 2;
      scale *= 32;
    }
    throw new BadCode('the code is damaged (a number is too long)');
  }

  /** A count in 0..max, or the code is damaged. */
  count(max: number, what: string): number {
    const n = this.int();
    if (n < 0 || n > max) throw new BadCode(`the code is damaged (${what})`);
    return n;
  }

  text(max: number): string {
    const n = this.count(max, 'text length');
    let s = '';
    for (let i = 0; i < n; i++) {
      const code = this.bits(6);
      s += code === TEXT_ESCAPE ? String.fromCharCode(this.bits(16)) : (TEXT[code] ?? '');
    }
    return s;
  }

  /** True when only zero padding is left before `end`. */
  atEnd(): boolean {
    if (this.end * 8 - this.pos >= 8) return false;
    while (this.pos < this.end * 8) if (this.bit() !== 0) return false;
    return true;
  }
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const B64_INDEX = new Map([...B64].map((c, i) => [c, i]));

/** Bytes as unpadded base64url. */
export function toBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const n = ((bytes[i] ?? 0) << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
    const chars = Math.min(4, Math.ceil(((bytes.length - i) * 8) / 6));
    for (let k = 0; k < chars; k++) out += B64[(n >> (18 - k * 6)) & 63];
  }
  return out;
}

/** Unpadded base64url to bytes; null for a stray character, an impossible length or loose end bits. */
export function fromBase64Url(text: string): Uint8Array | null {
  if (text.length % 4 === 1) return null;
  const out = new Uint8Array(Math.floor((text.length * 6) / 8));
  let acc = 0;
  let bits = 0;
  let o = 0;
  for (const ch of text) {
    const v = B64_INDEX.get(ch);
    if (v === undefined) return null;
    acc = ((acc << 6) | v) & 0xffffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[o++] = (acc >> bits) & 255;
    }
  }
  // The unused low bits of the last character must be zero, so each code has one spelling.
  return (acc & ((1 << bits) - 1)) === 0 ? out : null;
}

/** CRC-16/CCITT-FALSE: catches every error burst shorter than 16 bits (any one mistyped character). */
export function crc16(bytes: Uint8Array, length = bytes.length): number {
  let crc = 0xffff;
  for (let i = 0; i < length; i++) {
    crc ^= (bytes[i] ?? 0) << 8;
    for (let b = 0; b < 8; b++) crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
  }
  return crc;
}
